/* ===== Connect template — slideshow, promo cards, header sync ===== */
import { sanitizeImageSrc } from '../utils/url.js?v=82.86';
import {
  CLIENT_HEADER_SOCIAL_SVGS,
  getHeaderSocialPlatforms,
  isAlfaTemplateActive,
  isConnectTemplateActive,
  isCustomTemplateActive,
  syncTemplateHeaderBrand
} from './client-template-style.js?v=82.86';
import { openClientPromoGalleryOverlay, applyClientPromoGalleryUI } from './client-promo-gallery.js?v=82.86';
import { getCurrentSettings } from '../services/settings.js?v=82.86';
import { isDemoBackdropGroup, demoBackdropDataUri, demoBackdropColor, setDemoBackdropNote } from './demo-backdrop.js?v=82.86';
import { isLoyaltyVisible } from './loyalty-module.js?v=82.86';
import { isEventVisible } from './event-module.js?v=82.86';

let connectSlideTimer = null;
let connectSlideIndex = 0;

const CONNECT_ONLY_SELECTOR = '.client-connect-only';

function stopConnectSlideshow() {
  if (connectSlideTimer) {
    clearInterval(connectSlideTimer);
    connectSlideTimer = null;
  }
}

function setConnectOnlyVisible(visible) {
  document.querySelectorAll(CONNECT_ONLY_SELECTOR).forEach((el) => {
    if (visible) {
      el.removeAttribute('hidden');
      el.removeAttribute('aria-hidden');
      el.style.removeProperty('display');
    } else {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      el.style.setProperty('display', 'none', 'important');
    }
  });
}

const CONNECT_DOM_HIDDEN_IDS = [
  'layoutBlockPromo',
  'clientPremiumSectionPromo',
  'clientElegantSocialSection',
  'layoutBlockLoyalty',
  'layoutBlockEvent',
  'layoutBlockSocial',
  'layoutBlockRules'
];

const CONNECT_DOM_INLINE_IDS = [
  'clientHeroActions',
  'clientPremiumStage',
  'clientPremiumCenterCta',
  'socialIconsBar',
  ...CONNECT_DOM_HIDDEN_IDS
];

/** Elemente Connect care pot „scăpa” pe alte teme după vizită pe Connect. */
export const CONNECT_LEAK_IDS = [
  'clientConnectPromoRow',
  'clientConnectSlideshow',
  'clientConnectOfferCard',
  'clientConnectCampaignCard',
  'clientConnectSlideshowTrack',
  'clientConnectSlideshowDots',
  'locationMenuBtn',
  'feedbackBtn'
];

const CONNECT_INLINE_CLEAR_PROPS = [
  'display',
  'visibility',
  'opacity',
  'pointer-events',
  'position',
  'left',
  'top',
  'right',
  'bottom',
  'width',
  'min-width',
  'height',
  'min-height',
  'max-height',
  'max-width',
  'margin',
  'padding',
  'overflow',
  'flex',
  'order',
  'transform',
  'z-index',
  'background',
  'background-image',
  'background-size',
  'background-position',
  'background-repeat'
];

function clearConnectElementInlineStyles(el) {
  if (!el) return;
  CONNECT_INLINE_CLEAR_PROPS.forEach((prop) => el.style.removeProperty(prop));
}

/** Curăță inline-uri lăsate de Connect (duo, promo cards, slideshow). */
export function clearConnectIntruderStyles() {
  CONNECT_LEAK_IDS.forEach((id) => {
    clearConnectElementInlineStyles(document.getElementById(id));
  });

  document.querySelectorAll('.client-connect-card__media').forEach((media) => {
    media.classList.remove('has-image');
    media.replaceChildren();
    clearConnectElementInlineStyles(media);
  });

  ['locationMenuBtn', 'feedbackBtn'].forEach((id) => {
    const titles = document.querySelector(`#${id} .client-premium-module-card__titles`);
    if (titles) delete titles.dataset.connectLabel;
  });

  document.querySelectorAll('.client-connect-social-btn').forEach((btn) => {
    clearConnectElementInlineStyles(btn);
    btn.querySelectorAll('svg').forEach((svg) => {
      svg.style.removeProperty('display');
      svg.style.removeProperty('width');
      svg.style.removeProperty('height');
    });
  });
}

export function hideConnectOnlyChrome() {
  document.querySelectorAll(CONNECT_ONLY_SELECTOR).forEach((el) => {
    el.hidden = true;
    el.setAttribute('hidden', '');
    el.setAttribute('aria-hidden', 'true');
    clearConnectElementInlineStyles(el);
    el.style.setProperty('display', 'none', 'important');
  });

  ['clientConnectOfferCard', 'clientConnectCampaignCard', 'clientConnectPromoRow', 'clientConnectSlideshow'].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.hidden = true;
    el.setAttribute('hidden', '');
    el.setAttribute('aria-hidden', 'true');
    clearConnectElementInlineStyles(el);
    el.style.setProperty('display', 'none', 'important');
  });
}

export function restoreConnectTemplateDom() {
  stopConnectSlideshow();
  setConnectOnlyVisible(false);
  clearConnectIntruderStyles();
  hideConnectOnlyChrome();

  const hint = document.getElementById('clientConnectSocialHint');
  if (hint) {
    hint.textContent = '';
    hint.hidden = true;
    hint.setAttribute('hidden', '');
    hint.setAttribute('aria-hidden', 'true');
    clearConnectElementInlineStyles(hint);
  }

  CONNECT_DOM_INLINE_IDS.forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.hidden = false;
    el.removeAttribute('hidden');
    el.removeAttribute('aria-hidden');
    el.style.removeProperty('display');
    el.style.removeProperty('flex');
    el.style.removeProperty('min-height');
    el.style.removeProperty('padding-bottom');
  });

  const pubLogo = document.getElementById('clientPublicBrandLogo');
  if (pubLogo) {
    pubLogo.hidden = false;
    pubLogo.removeAttribute('hidden');
  }

  const menuBody = document.querySelector('#layoutBlockMenu .client-premium-menu__body');
  if (menuBody) menuBody.style.removeProperty('display');
}

function connectImageUrl(raw) {
  const safe = sanitizeImageSrc(String(raw || '').trim());
  if (safe) return safe;
  const trimmed = String(raw || '').trim();
  if (/^https:\/\//i.test(trimmed)) return trimmed;
  if (/^(data:image\/|blob:)/i.test(trimmed)) return trimmed;
  return '';
}

export function getLocationImages(settings = {}, maxImages = 5) {
  const cap = Math.max(1, Math.min(10, Number(maxImages) || 5));
  const urls = Array.isArray(settings.bgURLs) ? settings.bgURLs : [];
  if (urls.length) {
    return urls
      .map((u) => connectImageUrl(u))
      .filter(Boolean)
      .slice(0, cap);
  }
  const single = connectImageUrl(settings.bgURL);
  return single ? [single] : [];
}

/** Aceeași ordine ca overlay-ul galeriei promo (#clientPromoGalleryOverlayTrack). */
function getOfferImage(settings = {}) {
  const g = settings?.clientPromoGallery;
  if (!g || g.enabled !== true || !Array.isArray(g.images)) {
    return { url: '', count: 0 };
  }

  const urls = g.images
    .map((u) => connectImageUrl(u))
    .filter(Boolean)
    .slice(0, 10);

  if (urls.length) {
    return { url: urls[0], count: urls.length };
  }

  const track = document.getElementById('clientPromoGalleryOverlayTrack');
  const domImg = track?.querySelector('.client-promo-gallery__img');
  const domUrl = connectImageUrl(domImg?.currentSrc || domImg?.src || '');
  if (domUrl) {
    return { url: domUrl, count: track?.querySelectorAll('.client-promo-gallery__img').length || 1 };
  }

  return { url: '', count: 0 };
}

/** Prima imagine din premiile campaniei (aceeași sursă ca #prizeImg din popup). */
function getCampaignImage(settings = {}) {
  const prizes = Array.isArray(settings.prizes) ? settings.prizes : [];

  const stocked = prizes.find((p) => {
    const img = connectImageUrl(p?.image);
    if (!img) return false;
    const count = Number(p?.count);
    return !Number.isFinite(count) || count > 0;
  });
  if (stocked?.image) {
    const url = connectImageUrl(stocked.image);
    if (url) return url;
  }

  for (const p of prizes) {
    const url = connectImageUrl(p?.image);
    if (url) return url;
  }

  const defaultImg = connectImageUrl(settings.defaultPrizeImg);
  if (defaultImg) return defaultImg;

  return connectImageUrl(settings.confirmBgURL) || '';
}

function getCampaignImageCount(settings = {}) {
  const prizes = Array.isArray(settings.prizes) ? settings.prizes : [];
  return prizes.filter((p) => connectImageUrl(p?.image)).length;
}

function isWithinConnectDateRange(startDate, endDate) {
  const today = new Date().toISOString().slice(0, 10);
  const start = String(startDate || '').trim();
  const end = String(endDate || '').trim();
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
}

export function getConnectActiveModule(settings = {}) {
  const campaignActive = (settings.campaignStatus || 'draft') === 'active';
  if (campaignActive) {
    const title =
      String(settings.campaignTitle || settings.campaignName || '').trim() ||
      'CAMPANIA LUNII';
    const cta =
      String(settings.playButtonLabel || settings.buttonText || '').trim() ||
      'PARTICIPĂ ÎN CAMPANIE';
    return {
      kind: 'campaign',
      icon: '🎁',
      title,
      cta,
      imageUrl: getCampaignImage(settings),
      imageCount: getCampaignImageCount(settings),
      fallback: 'Campanie'
    };
  }

  if (isLoyaltyVisible(settings)) {
    const loyalty = settings.loyaltySettings || {};
    return {
      kind: 'loyalty',
      icon: '⭐',
      title: String(loyalty.title || '').trim() || 'FIDELIZARE',
      cta: String(loyalty.buttonText || '').trim() || 'ÎNREGISTREAZĂ ACHIZIȚIA',
      imageUrl: connectImageUrl(loyalty.promoImageUrl),
      imageCount: loyalty.promoImageUrl ? 1 : 0,
      fallback: 'Fidelizare'
    };
  }

  if (isEventVisible(settings)) {
    const event = settings.eventModule || {};
    const images = Array.isArray(event.images)
      ? event.images.map((url) => connectImageUrl(url)).filter(Boolean)
      : [];
    return {
      kind: 'event',
      icon: '🎉',
      title: String(event.title || '').trim() || 'EVENIMENT',
      cta: String(event.buttonLabel || '').trim() || 'DESCOPERĂ',
      imageUrl: images[0] || '',
      imageCount: images.length,
      fallback: 'Eveniment'
    };
  }

  return null;
}

export function renderConnectSlideshow(images, slideDurationSec = 5) {
  const section = document.getElementById('clientConnectSlideshow');
  const track = document.getElementById('clientConnectSlideshowTrack');
  const dots = document.getElementById('clientConnectSlideshowDots');
  if (!section || !track || !dots) return;

  stopConnectSlideshow();
  track.replaceChildren();
  dots.replaceChildren();
  connectSlideIndex = 0;

  // Demo fără poze: fundal minimalist + chenar „Aici apar pozele tale”
  let backdrop = false;
  if (!images.length) {
    const grp = (getCurrentSettings() || {}).demoBackdrop;
    if (isDemoBackdropGroup(grp)) {
      images = [demoBackdropDataUri(grp)];
      backdrop = true;
    }
  }
  setDemoBackdropNote(section, backdrop);
  section.classList.toggle('sj-has-backdrop', backdrop);

  if (!images.length) {
    section.hidden = true;
    section.setAttribute('aria-hidden', 'true');
    return;
  }

  section.hidden = false;
  section.removeAttribute('hidden');
  section.removeAttribute('aria-hidden');

  images.forEach((url, i) => {
    const slide = document.createElement('div');
    slide.className = 'client-connect-slideshow__slide';
    slide.style.backgroundImage = `url("${url.replace(/"/g, '\\"')}")`;
    if (backdrop) slide.style.backgroundColor = demoBackdropColor((getCurrentSettings() || {}).demoBackdrop);
    slide.dataset.index = String(i);
    if (i === 0) slide.classList.add('is-active');
    track.appendChild(slide);

    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'client-connect-slideshow__dot';
    dot.setAttribute('aria-label', `Slide ${i + 1}`);
    if (i === 0) dot.classList.add('is-active');
    dot.addEventListener('click', () => goConnectSlide(i, images.length));
    dots.appendChild(dot);
  });

  if (images.length <= 1) {
    dots.hidden = true;
    return;
  }

  dots.hidden = false;
  dots.removeAttribute('hidden');

  const sec = Math.max(2, Number(slideDurationSec) || 5);
  connectSlideTimer = setInterval(() => {
    goConnectSlide((connectSlideIndex + 1) % images.length, images.length);
  }, sec * 1000);
}

export function goConnectSlide(index, total) {
  connectSlideIndex = index;
  const track = document.getElementById('clientConnectSlideshowTrack');
  const dots = document.getElementById('clientConnectSlideshowDots');
  track?.querySelectorAll('.client-connect-slideshow__slide').forEach((el, i) => {
    el.classList.toggle('is-active', i === index);
  });
  dots?.querySelectorAll('.client-connect-slideshow__dot').forEach((el, i) => {
    el.classList.toggle('is-active', i === index);
  });

  if (document.getElementById('clientPage')?.classList.contains('template-alfa')) {
    import('./client-alfa-template.js?v=82.86')
      .then((m) => m.syncAlfaAmbientBg?.(index))
      .catch(() => {});
  }
}

function fillConnectCardMedia(mediaEl, badgeExtraEl, url, count, fallbackText) {
  if (!mediaEl) return;
  mediaEl.replaceChildren();
  mediaEl.classList.remove('has-image');
  mediaEl.style.removeProperty('background-image');

  if (url) {
    const safeUrl = url.replace(/"/g, '\\"');
    mediaEl.style.backgroundImage = `url("${safeUrl}")`;
    mediaEl.style.backgroundSize = 'cover';
    mediaEl.style.backgroundPosition = 'center center';
    mediaEl.style.backgroundRepeat = 'no-repeat';
    mediaEl.classList.add('has-image');

    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.decoding = 'async';
    img.loading = 'eager';
    img.className = 'client-connect-card__img';
    mediaEl.appendChild(img);

    if (badgeExtraEl && count > 1) {
      badgeExtraEl.textContent = `+${count - 1}`;
      badgeExtraEl.hidden = false;
    } else if (badgeExtraEl) {
      badgeExtraEl.hidden = true;
    }
  } else if (fallbackText) {
    const ph = document.createElement('span');
    ph.className = 'client-connect-card__fallback-text';
    ph.textContent = fallbackText;
    mediaEl.appendChild(ph);
    if (badgeExtraEl) badgeExtraEl.hidden = true;
  } else if (badgeExtraEl) {
    badgeExtraEl.hidden = true;
  }
}

export function syncConnectPromoRow(settings = {}) {
  const page = document.getElementById('clientPage');
  if (!isConnectTemplateActive(page) && !isAlfaTemplateActive(page) && !isCustomTemplateActive(page)) {
    return;
  }

  const row = document.getElementById('clientConnectPromoRow');
  const offerCard = document.getElementById('clientConnectOfferCard');
  const campaignCard = document.getElementById('clientConnectCampaignCard');
  if (!row || !offerCard || !campaignCard) return;

  const promo = settings.clientPromoGallery;
  const promoOn = promo?.enabled === true;
  const activeModule = getConnectActiveModule(settings);

  offerCard.hidden = !promoOn;
  campaignCard.hidden = !activeModule;
  if (promoOn) {
    offerCard.removeAttribute('hidden');
    offerCard.removeAttribute('aria-hidden');
    offerCard.style.removeProperty('display');
  }
  if (activeModule) {
    campaignCard.removeAttribute('hidden');
    campaignCard.removeAttribute('aria-hidden');
    campaignCard.style.removeProperty('display');
  }

  if (!promoOn && !activeModule) {
    row.hidden = true;
    row.setAttribute('aria-hidden', 'true');
    return;
  }

  row.hidden = false;
  row.removeAttribute('hidden');
  row.removeAttribute('aria-hidden');
  row.style.removeProperty('display');

  const offerTitle = document.getElementById('clientConnectOfferTitle');
  if (offerTitle) {
    offerTitle.textContent = String(promo?.title || 'OFERTA SĂPTĂMÂNII').trim().slice(0, 40).toUpperCase();
  }

  const { url: offerUrl, count: offerCount } = getOfferImage(settings);
  fillConnectCardMedia(
    document.getElementById('clientConnectOfferMedia'),
    document.getElementById('clientConnectOfferCount'),
    offerUrl,
    offerCount,
    'Ofertă'
  );

  const offerCta = offerCard.querySelector('.client-connect-card__cta');
  if (offerCta) offerCta.textContent = 'Vezi detalii ›';

  if (activeModule) {
    campaignCard.dataset.connectModule = activeModule.kind;
    const moduleIcon = campaignCard.querySelector('.client-connect-card__head-icon');
    if (moduleIcon) moduleIcon.textContent = activeModule.icon;

    const moduleTitle = campaignCard.querySelector('.client-connect-card__head-title');
    if (moduleTitle) {
      moduleTitle.textContent = activeModule.title.trim().slice(0, 40).toUpperCase();
    }

    const campaignCta = document.getElementById('clientConnectCampaignCta');
    if (campaignCta) {
      campaignCta.textContent = activeModule.cta.trim().slice(0, 40).toUpperCase() + ' ›';
    }

    fillConnectCardMedia(
      document.getElementById('clientConnectCampaignMedia'),
      document.getElementById('clientConnectCampaignCount'),
      activeModule.imageUrl,
      activeModule.imageCount,
      activeModule.fallback
    );
  }
}

export function syncConnectFooterLabel() {
  const wifi = document.querySelector('#clientWifiFab .client-fab-pill__label');
  if (wifi) wifi.textContent = 'WiFi';

  const loc = document.querySelector('#clientLocationMapFab .client-fab-pill__label');
  if (loc) loc.textContent = 'Locație';

  const hours = document.querySelector('#clientLocationHoursFab .client-fab-pill__label');
  if (hours) hours.textContent = 'Program';

  const contact = document.getElementById('clientReservationFabLabel');
  if (contact) contact.textContent = 'Contact';
}

function styleConnectSocialButton(btn, platformKey) {
  btn.classList.add('client-connect-social-btn');
  btn.style.setProperty('width', '22px', 'important');
  btn.style.setProperty('height', '22px', 'important');
  btn.style.setProperty('min-width', '22px', 'important');
  btn.style.setProperty('min-height', '22px', 'important');
  btn.style.setProperty('max-width', '22px', 'important');
  btn.style.setProperty('max-height', '22px', 'important');
  btn.style.setProperty('padding', '0', 'important');
  btn.style.setProperty('flex', '0 0 22px', 'important');
  btn.style.setProperty('border-radius', '50%', 'important');
  btn.style.setProperty('overflow', 'hidden', 'important');
  const svg = btn.querySelector('svg');
  if (svg) {
    svg.style.setProperty('width', '10px', 'important');
    svg.style.setProperty('height', '10px', 'important');
    svg.style.setProperty('display', 'block', 'important');
  }
  if (platformKey === 'facebook') {
    btn.style.setProperty('background', '#1877f2', 'important');
  } else if (platformKey === 'instagram') {
    btn.style.setProperty(
      'background',
      'linear-gradient(135deg, #f58529 0%, #dd2a7b 45%, #8134af 75%, #515bd4 100%)',
      'important'
    );
  } else if (platformKey === 'tiktok') {
    btn.style.setProperty('background', '#010101', 'important');
  }
}

export function syncConnectHeader(settings = {}, options = {}) {
  const header = document.getElementById('clientTemplateHeader');
  const socialRow = document.getElementById('clientTemplateSocialRow');
  const brandEl = document.getElementById('clientTemplateBrand');
  const hint = document.getElementById('clientConnectSocialHint');
  if (!header || !socialRow) return;

  syncTemplateHeaderBrand(settings);
  if (brandEl) {
    brandEl.hidden = true;
    brandEl.setAttribute('hidden', '');
    brandEl.textContent = '';
  }

  const platforms = getHeaderSocialPlatforms(settings);
  socialRow.replaceChildren();
  platforms.forEach((platform) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `client-template-header__social-btn client-template-header__social-btn--${platform.key} client-connect-social-btn`;
    btn.setAttribute('data-action', 'openExternalAction');
    btn.setAttribute('data-arg', platform.arg);
    btn.setAttribute('aria-label', platform.label);
    btn.innerHTML = CLIENT_HEADER_SOCIAL_SVGS[platform.key] || '';
    styleConnectSocialButton(btn, platform.key);
    socialRow.appendChild(btn);
  });

  if (hint) {
    const defaultHint = options.hintLeftAlign
      ? 'Urmărește-ne pe social media'
      : 'Urmărește-ne pe social media';
    hint.textContent = String(settings.socialCardHint || '').trim() || defaultHint;
    hint.hidden = false;
    hint.removeAttribute('hidden');
    if (options.hintLeftAlign) {
      hint.classList.add('client-alfa-social-hint');
    } else {
      hint.classList.remove('client-alfa-social-hint');
    }
  }

  header.hidden = false;
  header.removeAttribute('hidden');
  header.removeAttribute('aria-hidden');
}

function syncConnectDuoCopy(settings = {}) {
  const menuBtn = document.getElementById('locationMenuBtn');
  if (menuBtn) menuBtn.style.setProperty('display', 'flex', 'important');

  const menuLabel = String(settings.menuLabel || 'MENIU').trim().slice(0, 40).toUpperCase() || 'MENIU';
  const menuTitles = document.querySelector('#locationMenuBtn .client-premium-module-card__titles');
  if (menuTitles) menuTitles.dataset.connectLabel = menuLabel;

  const menuTitle = document.querySelector('#locationMenuBtn .location-menu-btn__text');
  if (menuTitle) menuTitle.textContent = menuLabel;

  const menuSub = document.getElementById('clientMenuCardSubtitle');
  if (menuSub) menuSub.textContent = '';

  const menuBody = document.querySelector('#layoutBlockMenu .client-premium-menu__body');
  if (menuBody) menuBody.style.setProperty('display', 'none', 'important');

  const menuLogo = document.getElementById('clientMenuCardLogo');
  if (menuLogo) {
    menuLogo.hidden = true;
    menuLogo.removeAttribute('src');
  }

  const feedbackBtn = document.getElementById('feedbackBtn');
  if (feedbackBtn) feedbackBtn.style.setProperty('display', 'flex', 'important');

  const reviewLabel =
    String(settings.feedbackButtonLabel || settings.feedbackCardTitle || 'Recenzii')
      .trim()
      .slice(0, 40)
      .toUpperCase() || 'RECENZII';
  const reviewTitles = document.querySelector('#feedbackBtn .client-premium-module-card__titles');
  if (reviewTitles) reviewTitles.dataset.connectLabel = reviewLabel;

  const feedbackTitle = document.querySelector('#feedbackBtn .feedback-title');
  if (feedbackTitle) {
    feedbackTitle.textContent = reviewLabel;
  }

  const reviewSub = document.getElementById('clientReviewCardSubtitle');
  if (reviewSub) reviewSub.textContent = '';
}

function openConnectOfferGallery(settings = {}) {
  if (openClientPromoGalleryOverlay()) return;
  applyClientPromoGalleryUI(settings || getCurrentSettings() || {});
  if (openClientPromoGalleryOverlay()) return;
  document.getElementById('clientPromoGalleryTrigger')?.click();
}

function openConnectCampaignModule(card) {
  const kind = card?.dataset?.connectModule || 'campaign';
  if (kind === 'loyalty') {
    document.getElementById('clientLoyaltyModuleTrigger')?.click();
    return;
  }
  if (kind === 'event') {
    document.getElementById('clientEventModuleTrigger')?.click();
    return;
  }
  import('../services/game.js?v=82.86')
    .then((m) => m.playGame?.())
    .catch(() => {});
}

export function bindConnectCardActions() {
  const offer = document.getElementById('clientConnectOfferCard');
  if (offer && offer.dataset.connectBound !== '1') {
    offer.dataset.connectBound = '1';
    offer.addEventListener('click', (e) => {
      e.preventDefault();
      openConnectOfferGallery(getCurrentSettings() || {});
    });
  }

  const campaign = document.getElementById('clientConnectCampaignCard');
  if (campaign && campaign.dataset.connectBound !== '1') {
    campaign.dataset.connectBound = '1';
    campaign.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openConnectCampaignModule(campaign);
    });
  }
}

export function applyConnectTemplateDomFixes() {
  const page = document.getElementById('clientPage');
  /* Nu apela restore aici — un sync întârziat pe altă temă ștergea display:contents (Avva) / layout. */
  if (!isConnectTemplateActive(page)) return;

  setConnectOnlyVisible(true);
  bindConnectCardActions();

  const hero = document.getElementById('clientHeroActions');
  if (hero) hero.style.setProperty('display', 'contents', 'important');

  const stage = document.getElementById('clientPremiumStage');
  if (stage) stage.style.setProperty('display', 'contents', 'important');

  ['layoutBlockPromo', 'clientPremiumSectionPromo', 'clientElegantSocialSection', 'layoutBlockLoyalty', 'layoutBlockEvent', 'layoutBlockSocial', 'layoutBlockRules'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      el.style.setProperty('display', 'none', 'important');
    }
  });

  const cta = document.getElementById('clientPremiumCenterCta');
  if (cta) cta.style.setProperty('display', 'none', 'important');

  const socialBar = document.getElementById('socialIconsBar');
  if (socialBar) socialBar.style.setProperty('display', 'none', 'important');

  const pubLogo = document.getElementById('clientPublicBrandLogo');
  if (pubLogo) pubLogo.hidden = true;

  const menuBody = document.querySelector('#layoutBlockMenu .client-premium-menu__body');
  if (menuBody) menuBody.style.setProperty('display', 'none', 'important');
}

export function stopConnectSlideshowTimer() {
  stopConnectSlideshow();
}

export function syncConnectClientCopy(settings = {}) {
  if (!isConnectTemplateActive()) return;

  syncConnectHeader(settings);
  renderConnectSlideshow(getLocationImages(settings, 5), settings.slideDuration || 5);
  syncConnectPromoRow(settings);
  syncConnectDuoCopy(settings);
  syncConnectFooterLabel();
}

export function syncConnectTemplate(settings = {}) {
  if (!isConnectTemplateActive()) return;
  applyConnectTemplateDomFixes();
  if (!isConnectTemplateActive()) return;
  applyClientPromoGalleryUI(settings);
  syncConnectClientCopy(settings);

  requestAnimationFrame(() => {
    if (!isConnectTemplateActive()) return;
    syncConnectPromoRow(settings);
    syncConnectDuoCopy(settings);
    syncConnectFooterLabel();
  });

  setTimeout(() => {
    if (!isConnectTemplateActive()) return;
    syncConnectPromoRow(settings);
    syncConnectFooterLabel();
  }, 120);
}
